import type { AgentPublicConfig } from '@cloneai/shared-types';

/**
 * State machine for the agent-runtime SPA.
 *
 * Transitions (P2-06 scope):
 *   loading → not-found | paused | ready
 *   ready → playing-video → minting-session → voice-active → ended
 *   ready → playing-video → text-active → ended
 *   playing-video | minting-session | voice-active → ready (retry) on failure
 *
 * P2-06 ships the loading/not-found/paused/ready transitions; the later
 * states are declared here so AgentStage can compile against them once
 * P2-10 / P2-08 wire the transitions in.
 */
export type AgentState =
  | { status: 'loading' }
  | { status: 'not-found'; reason: 'archived' | 'not-found' }
  | { status: 'paused'; agentName: string }
  | { status: 'ready'; config: AgentPublicConfig; retryHint?: boolean }
  | { status: 'playing-video'; config: AgentPublicConfig }
  | { status: 'minting-session'; config: AgentPublicConfig }
  | { status: 'voice-active'; config: AgentPublicConfig }
  | { status: 'text-active'; config: AgentPublicConfig }
  | { status: 'ended'; config: AgentPublicConfig };

export type AgentEvent =
  | { type: 'CONFIG_LOADED'; config: AgentPublicConfig }
  | { type: 'NOT_FOUND'; reason: 'archived' | 'not-found' }
  | { type: 'PAUSED'; agentName: string }
  | { type: 'VIDEO_START' }
  | { type: 'VIDEO_END' }
  | { type: 'SESSION_MINT_START' }
  | { type: 'SESSION_MINT_DONE' }
  | { type: 'SWITCH_TO_TEXT' }
  | { type: 'SESSION_FAILED' }
  | { type: 'SESSION_ENDED' };

export function agentReducer(state: AgentState, event: AgentEvent): AgentState {
  switch (event.type) {
    case 'CONFIG_LOADED':
      return { status: 'ready', config: event.config };
    case 'NOT_FOUND':
      return { status: 'not-found', reason: event.reason };
    case 'PAUSED':
      return { status: 'paused', agentName: event.agentName };
    case 'VIDEO_START':
      if (state.status === 'ready') return { status: 'playing-video', config: state.config };
      return state;
    case 'VIDEO_END':
      if (state.status === 'playing-video') {
        return state.config.interactionMode === 'text'
          ? { status: 'text-active', config: state.config }
          : { status: 'minting-session', config: state.config };
      }
      return state;
    case 'SESSION_MINT_START':
      if (state.status === 'ready') return { status: 'minting-session', config: state.config };
      return state;
    case 'SESSION_MINT_DONE':
      if (state.status === 'minting-session')
        return { status: 'voice-active', config: state.config };
      return state;
    case 'SWITCH_TO_TEXT':
      if (
        state.status === 'playing-video' ||
        state.status === 'minting-session' ||
        state.status === 'voice-active'
      ) {
        return { status: 'text-active', config: state.config };
      }
      return state;
    case 'SESSION_FAILED':
      if (
        state.status === 'playing-video' ||
        state.status === 'minting-session' ||
        state.status === 'voice-active' ||
        state.status === 'text-active'
      ) {
        return { status: 'ready', config: state.config, retryHint: true };
      }
      return state;
    case 'SESSION_ENDED':
      if (
        state.status === 'playing-video' ||
        state.status === 'minting-session' ||
        state.status === 'voice-active' ||
        state.status === 'text-active'
      ) {
        return { status: 'ended', config: state.config };
      }
      return state;
  }
}

export const initialState: AgentState = { status: 'loading' };
